import { merge } from 'lodash';

import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { Injectable } from '@angular/core';

import { SnOrganization, SnContentType } from '@lib/api/entities';
import { OrganizationsAPI } from '@lib/api/services';
import { SnCurrentOrganization } from '@lib/auth/services/current-organization.service';
import { SnFormFieldConfig } from '@lib/form';
import { ISnCheckboxFormFieldConfig } from '@lib/form/checkbox';
import { ISnChipsAutocompleteFormFieldConfig } from '@lib/form/chips-autocomplete';
import { ISnDetailedCheckboxFormFieldConfig } from '@lib/form/detailed-checkbox';
import { ISnSearchListFormFieldConfig } from '@lib/form/search-list';
import { t } from '@lib/translation';

import { FUSUS_CROSS_REGION_DEV } from '../constants';

@Injectable({ providedIn: 'root' })
export class SnShareFieldsGeneratorService {
  constructor(
    private readonly _organizationsAPI: OrganizationsAPI,
    private readonly _currentOrganization: SnCurrentOrganization,
  ) {}

  public getRestrictedField(
    fieldConfig: Partial<ISnCheckboxFormFieldConfig> = {},
  ): ISnCheckboxFormFieldConfig {
    const baseFieldConfig: ISnCheckboxFormFieldConfig = {
      key: 'isRestricted',
      type: 'checkbox',
      wrappers: [],
      className: 'sn-form-content',
      templateOptions: {
        appearance: 'fill',
        label: t('shares.conditionalShare'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getRestrictedFieldV2(
    fieldConfig: Partial<ISnDetailedCheckboxFormFieldConfig> = {},
  ): ISnDetailedCheckboxFormFieldConfig {
    const baseFieldConfig: ISnDetailedCheckboxFormFieldConfig = {
      key: 'isRestricted',
      type: 'detailed-checkbox',
      className: 'sn-form-content',
      defaultValue: false,
      templateOptions: {
        appearance: 'fill',
        mainLabel: t('shares.conditionalShare'),
        secondaryText: t('shares.cameraPlaybackWillOnlyBeAvailable'),
        icon: 'schedule',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getPlaybackText(fieldConfig: Partial<SnFormFieldConfig> = {}): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      template:
        `<i class="muted-text">${t('shares.playbackRestrictionText')}</i>`,
    };
    return merge(fieldConfig, baseFieldConfig);
  }

  public getDataDisclosureField(fieldConfig: Partial<SnFormFieldConfig> = {}): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      template: `
      <div class="muted-text">
        <p><strong>${t('shares.dataDisclosure.agreement')}</strong></p>
        <ul>
          <li>${t('shares.dataDisclosure.visibleData.cameraInfo')}</li>
          <li>${t('shares.dataDisclosure.visibleData.organizationName')}</li>
          <li>${t('shares.dataDisclosure.visibleData.mediaAccess')}</li>
          <li>${t('shares.dataDisclosure.visibleData.auditData')}</li>
        </ul>
        <p><strong>${t('shares.dataDisclosure.recipientMay')}:</strong></p>
        <ul>
          <li>${t('shares.dataDisclosure.recipientActions.saveMedia')}</li>
          <li>${t('shares.dataDisclosure.recipientActions.storeEvidence')}</li>
          <li>${t('shares.dataDisclosure.recipientActions.retainPolicy')}</li>
          <li>${t('shares.dataDisclosure.recipientActions.continueAccess')}</li>
        </ul>
      </div>
    `,
    };
    return merge(fieldConfig, baseFieldConfig);
  }

  public getViewLiveVideoField(
    fieldConfig: Partial<ISnCheckboxFormFieldConfig> = {},
  ): ISnCheckboxFormFieldConfig {
    const baseFieldConfig: ISnCheckboxFormFieldConfig = {
      key: 'permissionsDetails.viewLiveVideo',
      type: 'checkbox',
      className: 'sn-form-content',
      defaultValue: true,
      templateOptions: {
        appearance: 'outline',
        label: t('shares.viewLiveVideo'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getViewLiveVideoFieldV2(
    fieldConfig: Partial<ISnDetailedCheckboxFormFieldConfig> = {},
  ): ISnDetailedCheckboxFormFieldConfig {
    const baseFieldConfig: ISnDetailedCheckboxFormFieldConfig = {
      key: 'permissionsDetails.viewLiveVideo',
      type: 'detailed-checkbox',
      className: 'sn-form-content',
      defaultValue: true,
      templateOptions: {
        appearance: 'outline',
        mainLabel: t('shares.viewLiveVideo'),
        secondaryText: t('shares.accessRealTimeCameraStream'),
        icon: 'visibility',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getViewRecordingsField(
    fieldConfig: Partial<ISnCheckboxFormFieldConfig> = {},
  ): ISnCheckboxFormFieldConfig {
    const baseFieldConfig: ISnCheckboxFormFieldConfig = {
      key: 'permissionsDetails.viewPlayback',
      type: 'checkbox',
      className: 'sn-form-content',
      defaultValue: true,
      templateOptions: {
        appearance: 'outline',
        label: t('shares.viewRecordings'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getViewRecordingsFieldV2(
    fieldConfig: Partial<ISnDetailedCheckboxFormFieldConfig> = {},
  ): ISnDetailedCheckboxFormFieldConfig {
    const baseFieldConfig: ISnDetailedCheckboxFormFieldConfig = {
      key: 'permissionsDetails.viewPlayback',
      type: 'detailed-checkbox',
      className: 'sn-form-content',
      defaultValue: false,
      templateOptions: {
        appearance: 'outline',
        mainLabel: t('shares.viewRecordings'),
        secondaryText: t('shares.accessRecordedFootageAndTimeline'),
        icon: 'play_arrow',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getEnablePTZField(
    fieldConfig: Partial<ISnCheckboxFormFieldConfig> = {},
  ): ISnCheckboxFormFieldConfig {
    const baseFieldConfig: ISnCheckboxFormFieldConfig = {
      key: 'permissionsDetails.enablePtzControl',
      type: 'checkbox',
      className: 'sn-form-content',
      defaultValue: false,
      templateOptions: {
        appearance: 'outline',
        label: t('shares.enablePTZControls'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getEnablePTZFieldV2(
    fieldConfig: Partial<ISnDetailedCheckboxFormFieldConfig> = {},
  ): ISnDetailedCheckboxFormFieldConfig {
    const baseFieldConfig: ISnDetailedCheckboxFormFieldConfig = {
      key: 'permissionsDetails.enablePtzControl',
      type: 'detailed-checkbox',
      className: 'sn-form-content',
      defaultValue: false,
      templateOptions: {
        appearance: 'outline',
        mainLabel: t('shares.controlPTZ'),
        secondaryText: t('shares.panTiltZoomCameraControls'),
        icon: 'refresh',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getAdminOnlyField(
    fieldConfig: Partial<ISnCheckboxFormFieldConfig> = {},
  ): ISnCheckboxFormFieldConfig {
    const baseFieldConfig: ISnCheckboxFormFieldConfig = {
      key: 'isAdminOnly',
      type: 'checkbox',
      className: 'sn-form-content',
      defaultValue: false,
      templateOptions: {
        appearance: 'outline',
        label: t('shares.adminOnly'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getAdminOnlyFieldV2(
    fieldConfig: Partial<ISnDetailedCheckboxFormFieldConfig> = {},
  ): ISnDetailedCheckboxFormFieldConfig {
    const baseFieldConfig: ISnDetailedCheckboxFormFieldConfig = {
      key: 'isAdminOnly',
      type: 'detailed-checkbox',
      className: 'sn-form-content',
      defaultValue: false,
      templateOptions: {
        appearance: 'outline',
        mainLabel: t('shares.adminOnly'),
        secondaryText: t('shares.restrictsCameraVisibilityToUsers'),
        icon: 'shield',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }


  public getUserChangeOwnerField(
    fieldConfig: Partial<ISnCheckboxFormFieldConfig> = {},
  ): ISnCheckboxFormFieldConfig {
    const baseFieldConfig: ISnCheckboxFormFieldConfig = {
      key: 'userChangeOwner',
      type: 'checkbox',
      wrappers: [],
      className: 'sn-form-content',
      templateOptions: {
        appearance: 'fill',
        label: t('shares.changeOwner'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getOrganizationAutocompleteField(
    fieldConfig: Partial<ISnChipsAutocompleteFormFieldConfig<SnOrganization>>,
  ): ISnChipsAutocompleteFormFieldConfig {
    const baseFieldConfig: ISnChipsAutocompleteFormFieldConfig = {
      key: 'shares',
      type: 'chips-autocomplete',
      templateOptions: {
        label: t('shares.inviteOrganization'),
        placeholder: t('shares.enterOrganizationName'),
        appearance: 'outline',
        labelProp: 'name',
        valueProp: 'id',
        separatorKeysCodes: [ENTER, COMMA],
        fetchDataFn: (params) => this._organizationsAPI.getOrderedList(params),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getOrganizationAutocompleteFieldV2(
    fieldConfig: Partial<ISnSearchListFormFieldConfig<SnOrganization>> = {},
  ): ISnSearchListFormFieldConfig<SnOrganization> {
    const baseFieldConfig: ISnSearchListFormFieldConfig<SnOrganization> = {
      key: 'shares',
      type: 'search-list',
      templateOptions: {
        label: t('shares.shareForm.selectOrganization'),
        hideLabel: true,
        searchPlaceholder: t('shares.searchPlaceholder'),
        appearance: 'outline',
        labelProp: 'name',
        valueProp: 'id',
        multiple: true,
        showCheckboxes: true,
        maxHeight: '350px',
        filterLocally: true,
        debounceTime: 300,
        noResultsText: t('shares.noOrganizationsFound'),
        fetchDataFn: (params) => this._organizationsAPI.getOrderedList(params),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getStartDateField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      key: 'startDate',
      type: 'datetimepicker',
      templateOptions: {
        appearance: 'outline',
        label: t('shares.from'),
        placeholder: t('shares.datePlaceholder'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getEndDateField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      key: 'endDate',
      type: 'datetimepicker',
      templateOptions: {
        appearance: 'outline',
        label: t('common.to'),
        placeholder: t('shares.datePlaceholder'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getAdditionalFields(contentType: SnContentType): SnFormFieldConfig[] {
    const isFususCrossRegionDevEnabled = this._currentOrganization.settings[FUSUS_CROSS_REGION_DEV];

    switch (contentType) {
      case SnContentType.Camera:
      case SnContentType.Location:
      case SnContentType.Grid:
      case SnContentType.Tag:
        return isFususCrossRegionDevEnabled ? [
          this.getViewRecordingsFieldV2(),
          this.getViewLiveVideoFieldV2(),
          this.getRestrictedFieldV2(),
          this.getEnablePTZFieldV2(),
          this.getAdminOnlyFieldV2(),
          this.getDataDisclosureField(),
        ] : [
          this.getRestrictedField(),
          this.getPlaybackText(),
          this.getViewLiveVideoField(),
          this.getViewRecordingsField(),
          this.getEnablePTZField(),
          this.getAdminOnlyField(),
        ];

      case SnContentType.User:
        return [this.getUserChangeOwnerField()];

      default:
        return [];
    }
  }
}
